import React, { useRef, useCallback, useMemo, useState } from 'react'

interface VideoPlayerProps {
  src: string
  onTimeUpdate?: (currentTime: number) => void
  onEnded?: () => void
}

// Extract YouTube video ID from URL
const getYouTubeVideoId = (url: string): string | null => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/
  ]

  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match && match[1]) {
      return match[1]
    }
  }

  return null
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  onTimeUpdate,
  onEnded,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current && onTimeUpdate) {
      onTimeUpdate(videoRef.current.currentTime)
    }
  }, [onTimeUpdate])

  const handleEnded = useCallback(() => {
    if (onEnded) {
      onEnded()
    }
  }, [onEnded])

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => {
        setIsFullscreen(true)
      }).catch((err) => {
        console.error('Error attempting to enable fullscreen:', err)
      })
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false)
      })
    }
  }, [])

  // Check if URL is YouTube
  const youtubeVideoId = useMemo(() => getYouTubeVideoId(src), [src])
  const isYouTube = !!youtubeVideoId

  // Render YouTube iframe if YouTube URL
  if (isYouTube) {
    const embedUrl = `https://www.youtube.com/embed/${youtubeVideoId}?rel=0&modestbranding=1&playsinline=1`

    return (
      <div ref={containerRef} className="w-full relative group bg-black" style={{ aspectRatio: '16/9' }}>
        <iframe
          src={embedUrl}
          title="YouTube Video Player"
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ border: 'none' }}
        />
        {/* Fullscreen Button */}
        <button
          onClick={toggleFullscreen}
className="absolute top-4 right-4 z-10 flex items-center justify-center w-10 h-10 rounded-lg bg-black/60 backdrop-blur-sm text-gray-900 dark:text-white sm:opacity-0 sm:group-hover:opacity-100 hover:bg-black/80 transition-all duration-200"
          aria-label="Toggle Fullscreen"
        >
          <span className="material-symbols-outlined text-xl">
            {isFullscreen ? 'fullscreen_exit' : 'fullscreen'}
          </span>
        </button>
      </div>
    )
  }

  // Render standard video player for MP4/other formats
  return (
    <div ref={containerRef} className="w-full relative group bg-black" style={{ aspectRatio: '16/9' }}>
      <video
        ref={videoRef}
        src={src}
        controls
        controlsList="nodownload"
        className="w-full h-full object-contain"
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        playsInline
      >
        <track kind="captions" />
        Your browser does not support the video tag.
      </video>
      {/* Fullscreen Button */}
      <button
        onClick={toggleFullscreen}
        className="absolute top-4 right-4 z-10 flex items-center justify-center w-10 h-10 rounded-lg bg-black/60 backdrop-blur-sm text-gray-900 dark:text-white opacity-0 group-hover:opacity-100 hover:bg-black/80 transition-all duration-200"
        aria-label="Toggle Fullscreen"
      >
        <span className="material-symbols-outlined text-xl">
          {isFullscreen ? 'fullscreen_exit' : 'fullscreen'}
        </span>
      </button>
    </div>
  )
}

export default VideoPlayer
